import { useEffect, useRef, type CSSProperties } from 'react';

/**
 * Prairiefire brand tile — light periwinkle dotted waves on transparent,
 * sized to the asset's native 488×454 repeat.
 */
export const GEOMETRIC_PATTERN_URL =
  'https://media.multihub.io/property/6767906449167620/01a13652-3413-71a9-927b-2d5e11618c0a.webp';

export const GEOMETRIC_PATTERN_SIZE = '488px 454px';

/** Fraction of scroll speed the pattern follows; lower = stronger parallax. */
const PARALLAX_FACTOR = 0.4;

const patternStyle: CSSProperties = {
  backgroundImage: `url(${GEOMETRIC_PATTERN_URL})`,
  backgroundRepeat: 'repeat',
  backgroundSize: GEOMETRIC_PATTERN_SIZE,
  backgroundPosition: 'left top',
  willChange: 'background-position',
};

/**
 * Tile origin is pinned to page coordinates (offset by scroll), so stacked
 * sections using this background show one continuous pattern.
 */
export function GeometricPatternBackground() {
  const patternRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = patternRef.current;
    if (!el) return;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const factor = reduceMotion ? 0 : PARALLAX_FACTOR;
    let frame = 0;

    const update = () => {
      frame = 0;
      const scrollY = window.scrollY;
      const pageTop = el.getBoundingClientRect().top + scrollY;
      el.style.backgroundPosition = `left ${scrollY * factor - pageTop}px`;
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <div
      ref={patternRef}
      aria-hidden="true"
      className="pointer-events-none absolute inset-0"
      style={patternStyle}
    />
  );
}
